import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import api from "@/lib/api";
import {
  Beaker,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  UserPlus,
  FileEdit,
  Calculator,
  FileText,
  History,
  BookOpen,
  FlaskConical,
  Layers,
  Map,
  Wrench,
  Microscope,
  TestTube,
  Container,
  Users,
  Stethoscope,
  Building2,
  BarChart3,
  Wallet,
  LogOut,
  LogIn,
  Menu,
  X,
  Home,
  DollarSign,
  FolderOpen,
  Settings,
  Database,
  Shield,
  UserCog,
  Lock,
  FileImage,
  Receipt,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Menu items with required permissions
const menuItems = [
  {
    title: "Recepción",
    icon: ClipboardList,
    requiredPermissions: ["reception.create_patient", "reception.create_order", "reception.capture_results"],
    items: [
      { title: "Ingresar Nuevo Paciente", href: "/reception/new-patient", icon: UserPlus, permission: "reception.create_patient" },
      { title: "Captura de Resultados", href: "/reception/results", icon: FileEdit, permission: "reception.capture_results" },
      { title: "Editar Solicitud", href: "/reception/edit-request", icon: FileEdit, permission: "reception.edit_order" },
      { title: "Cotización", href: "/reception/quotation", icon: Calculator, permission: "reception.create_order" },
      { title: "Orden de Trabajo", href: "/reception/work-order", icon: FileText, permission: "reception.create_order" },
      { title: "Historial", href: "/reception/history", icon: History, permission: "reception.create_order" },
      { title: "Expedientes", href: "/reception/records", icon: FolderOpen, permission: "reception.create_patient" },
    ],
  },
  {
    title: "Catálogos",
    icon: BookOpen,
    requiredPermissions: ["catalogs.view"],
    items: [
      { title: "Estudios", href: "/catalogs/studies", icon: FlaskConical, permission: "catalogs.view" },
      { title: "Analitos", href: "/catalogs/analytes", icon: Microscope, permission: "catalogs.view" },
      { title: "Cálculos de Analitos", href: "/catalogs/analyte-formulas", icon: Calculator, permission: "catalogs.view" },
      { title: "Perfiles", href: "/catalogs/profiles", icon: Layers, permission: "catalogs.view" },
      { title: "Áreas", href: "/catalogs/areas", icon: Map, permission: "catalogs.view" },
      { title: "Métodos", href: "/catalogs/methods", icon: Microscope, permission: "catalogs.view" },
      { title: "Tipos de Muestras", href: "/catalogs/sample-types", icon: TestTube, permission: "catalogs.view" },
      { title: "Recipientes", href: "/catalogs/containers", icon: Container, permission: "catalogs.view" },
      { title: "Pacientes", href: "/catalogs/patients", icon: Users, permission: "catalogs.view" },
      { title: "Doctores", href: "/catalogs/doctors", icon: Stethoscope, permission: "catalogs.view" },
      { title: "Compañías", href: "/catalogs/companies", icon: Building2, permission: "catalogs.view" },
      { title: "Listas de Precios", href: "/catalogs/price-lists", icon: DollarSign, permission: "catalogs.view" },
    ],
  },
  {
    title: "Reportes",
    icon: BarChart3,
    requiredPermissions: ["reports.view"],
    items: [
      { title: "Estudios", href: "/reports/studies", icon: BarChart3, permission: "reports.view" },
      { title: "Arqueo", href: "/reports/arqueo", icon: Wallet, permission: "reports.view" },
    ],
  },
  {
    title: "Administrador",
    icon: Shield,
    requiredPermissions: ["admin.settings", "admin.users", "admin.roles", "admin.backup"],
    items: [
      { title: "Configuración", href: "/admin/settings", icon: Settings, permission: "admin.settings" },
      { title: "Pantalla de Inicio", href: "/admin/login-config", icon: LogIn, permission: "admin.settings" },
      { title: "Usuarios", href: "/admin/users", icon: UserCog, permission: "admin.users" },
      { title: "Sucursales", href: "/admin/branches", icon: Building2, permission: "admin.users" },
      { title: "Roles y Permisos", href: "/admin/roles", icon: Lock, permission: "admin.roles" },
      { title: "Formato de Impresión", href: "/admin/print-format", icon: FileImage, permission: "admin.settings" },
      { title: "Formato de Recibo", href: "/admin/receipt-format", icon: Receipt, permission: "admin.settings" },
      { title: "Formato de Cotización", href: "/admin/quote-format", icon: FileText, permission: "admin.settings" },
      { title: "Respaldo de Datos", href: "/admin/backup", icon: Database, permission: "admin.backup" },
    ],
  },
];

const NavItem = ({ item, isActive }) => {
  const Icon = item.icon;
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    // Navegar con un parámetro de refresh para forzar la recarga de datos
    const refreshParam = `?_r=${Date.now()}`;
    navigate(item.href + refreshParam);
  };
  
  return (
    <a
      href={item.href}
      onClick={handleClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-200 cursor-pointer",
        isActive
          ? "bg-secondary text-secondary-foreground font-medium"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
      data-testid={`nav-${item.href.replace(/\//g, '-').slice(1)}`}
    >
      <Icon className="h-4 w-4" />
      <span>{item.title}</span>
    </a>
  );
};

const NavGroup = ({ group, openGroups, setOpenGroups, userPermissions }) => {
  const location = useLocation();
  
  // Filter items based on user permissions
  const visibleItems = group.items.filter(item => {
    if (!item.permission) return true;
    return userPermissions?.includes(item.permission);
  });
  
  // Don't render the group if no items are visible
  if (visibleItems.length === 0) return null;
  
  const isOpen = openGroups.includes(group.title);
  const hasActiveChild = visibleItems.some((item) => location.pathname === item.href);
  const Icon = group.icon;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          // Close all other groups and open only this one (accordion behavior)
          setOpenGroups([group.title]);
        } else {
          // Allow closing if clicking on an already open group
          setOpenGroups([]);
        }
      }}
    >
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            "flex items-center justify-between w-full px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200",
            hasActiveChild
              ? "bg-primary/5 text-primary"
              : "text-foreground hover:bg-muted"
          )}
          data-testid={`nav-group-${group.title.toLowerCase()}`}
        >
          <div className="flex items-center gap-3">
            <Icon className="h-4 w-4" />
            <span>{group.title}</span>
          </div>
          {isOpen || hasActiveChild ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-4 mt-1 space-y-1">
        {visibleItems.map((item) => (
          <NavItem
            key={item.href}
            item={item}
            isActive={location.pathname === item.href}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboardLogo, setDashboardLogo] = useState(null);
  const [labName, setLabName] = useState("ComaLab");

  // Open the correct group based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    for (const group of menuItems) {
      const hasActiveItem = group.items.some(item => currentPath === item.href);
      if (hasActiveItem) {
        setOpenGroups([group.title]);
        return;
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get("/login-config");
        if (response.data.dashboard_logo_url) {
          setDashboardLogo(response.data.dashboard_logo_url);
        }
        if (response.data.lab_name) {
          setLabName(response.data.lab_name);
        }
      } catch (error) {
        console.error("Error loading config:", error);
      }
    };
    fetchConfig();
  }, []);

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  const getRoleName = (role) => {
    const roles = {
      admin: "Administrador",
      receptionist: "Recepcionista",
      technician: "Técnico",
    };
    return roles[role] || role;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-border transition-transform duration-300 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-28 px-3 border-b border-border">
            <Link to="/" className="flex items-center justify-center flex-1">
              <img
                src={dashboardLogo || "/logo-dashboard.jpg"}
                alt={labName}
                className="h-24 w-full max-w-[240px] object-contain"
              />
            </Link>
            <button
              className="lg:hidden p-1"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <div className="space-y-1 mb-4">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/?_r=${Date.now()}`);
                }}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 cursor-pointer",
                  location.pathname === "/"
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                )}
                data-testid="nav-dashboard"
              >
                <Home className="h-4 w-4" />
                <span>Dashboard</span>
              </a>
            </div>
            <div className="space-y-2">
              {menuItems.map((group) => (
                <NavGroup
                  key={group.title}
                  group={group}
                  openGroups={openGroups}
                  setOpenGroups={setOpenGroups}
                  userPermissions={user?.permissions || []}
                />
              ))}
            </div>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-muted transition-colors" data-testid="user-menu-trigger">
                  <Avatar className="h-9 w-9 bg-primary">
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {getRoleName(user?.role)}
                    </p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="text-destructive focus:text-destructive"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex items-center h-16 px-4 bg-white border-b border-border lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2 ml-3">
            <img
              src={dashboardLogo || "/logo-dashboard.jpg"}
              alt={labName}
              className="h-8 max-w-[140px] object-contain"
            />
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 lg:p-8" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
